import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeftRight, BadgeDollarSign, Package2, RefreshCcw, ShieldCheck, Sparkles } from 'lucide-react';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
            {note ? <p className="mt-1 text-xs text-slate-500">{note}</p> : null}
          </div>
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-700">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BudgetUsers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [activities, setActivities] = useState([]);
  const [activityUser, setActivityUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (nextPage = page) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(nextPage));
      params.set('limit', '20');
      const r = await api.get(`/admin/budget/users?${params.toString()}`);
      setItems(r.data?.users || []);
      setSummary(r.data?.summary || {});
      setPage(r.data?.page || nextPage);
      setPages(r.data?.pages || 1);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load budget users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(1); }, []);

  const loadActivity = async (user) => {
    setActivityUser(user || null);
    if (!user?.id) {
      setActivities([]);
      return;
    }
    setActivityLoading(true);
    try {
      const r = await api.get(`/admin/budget/activity?user_id=${encodeURIComponent(user.id)}&limit=8`);
      setActivities(r.data?.activities || []);
    } catch (err) {
      setActivities([]);
      toast.error(err.response?.data?.detail || 'Failed to load budget activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const activeItems = useMemo(() => items.filter((item) => item.budget_enabled), [items]);

  const doAction = async (url, payload, message) => {
    try {
      await api.post(url, payload);
      toast.success(message);
      await load(page);
      if (activityUser?.id && url.includes(`/admin/users/${activityUser.id}/`)) {
        await loadActivity(activityUser);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const onSearch = (event) => {
    event.preventDefault();
    setPage(1);
    load(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Users</h1>
          <p className="text-slate-500 text-sm mt-0.5">Users with Budget access, package details, trials and usage at a glance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/budget-packages')}>
            <Package2 size={14} className="mr-1.5" /> Budget Packages
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/subscriptions')}>
            <BadgeDollarSign size={14} className="mr-1.5" /> Subscriptions
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => load(page)} disabled={refreshing}>
            <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCard({ label: 'Budget Users', value: summary.budget_enabled_users ?? activeItems.length ?? 0, icon: ShieldCheck, note: 'Users currently eligible for Budget features.' })}
        {metricCard({ label: 'Active Budget Users', value: summary.active_users ?? 0, icon: Sparkles, note: 'Active subscriptions or trials.' })}
        {metricCard({ label: 'Trial Users', value: summary.trial_users ?? 0, icon: Package2, note: 'Users still within trial access.' })}
        {metricCard({ label: 'Usage Records', value: (summary.transactions ?? 0) + (summary.loans ?? 0) + (summary.credit ?? 0), icon: ArrowLeftRight, note: 'Transactions, loans and credit records.' })}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Access Overview</CardTitle>
          <form onSubmit={onSearch} className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or phone"
                className="bg-white"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {['all', 'active', 'trial', 'expired', 'cancelled', 'disabled'].map((item) => (
                <Button
                  key={item}
                  type="button"
                  variant={status === item ? 'default' : 'outline'}
                  className={status === item ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
                  onClick={() => {
                    setStatus(item);
                    setPage(1);
                    setTimeout(() => load(1), 0);
                  }}
                >
                  {item === 'all' ? 'All' : item.charAt(0).toUpperCase() + item.slice(1)}
                </Button>
              ))}
            </div>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Search</Button>
          </form>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Total users: {total}</div>
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Budget enabled: {summary.budget_enabled_users ?? 0}</div>
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Reports: {summary.reports ?? 0}</div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Trial Ends</TableHead>
                <TableHead>Subscription Ends</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Audit</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{user.name}</div>
                    <div className="text-xs text-slate-500">{user.email || user.phone || user.id}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{user.package_name || 'Free'}</div>
                    <div className="text-xs text-slate-500">{user.subscription_status || '-'}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.budget_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                      {user.budget_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <div className="mt-2 text-xs text-slate-500">
                      {user.days_left > 0 ? `${user.days_left} days left` : 'No active budget trial'}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(user.trial_end)}</TableCell>
                  <TableCell>{formatDate(user.subscription_end)}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div>Txns: {user.usage?.transactions ?? 0}</div>
                    <div>Branches: {user.usage?.branches ?? 0}</div>
                    <div>Loans: {user.usage?.loans ?? 0}</div>
                    <div>Credit: {user.usage?.credit ?? 0}</div>
                  </TableCell>
                  <TableCell>
                    <Button type="button" variant="outline" size="sm" title="View recent budget activity for this user" onClick={() => loadActivity(user)}>
                      View Activity
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/admin/subscriptions')}
                      >
                        View Subscription
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const packageId = window.prompt('Enter package ID or code to assign Budget access:', user.package_name || '');
                          if (packageId) doAction(`/admin/users/${user.id}/package`, { package_id: packageId }, 'Package assigned');
                        }}
                      >
                        Assign Package
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const days = Number(window.prompt('Extend trial by how many days?', '14') || 0);
                          if (days > 0) doAction(`/admin/users/${user.id}/subscription/trial`, { days }, 'Trial extended');
                        }}
                      >
                        Extend Trial
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          if (window.confirm(`Disable Budget access for ${user.name}? This will cancel the user's current subscription.`)) {
                            doAction(`/admin/users/${user.id}/subscription/cancel`, {}, 'Budget access disabled');
                          }
                        }}
                      >
                        Disable Budget
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-10">
                    No budget users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {activityUser ? `${activityUser.name}'s Budget Activity` : 'Budget Activity Drilldown'}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {activityUser ? 'Recent package, trial and subscription actions for this user.' : 'Select a user and click Activity to see their recent Budget history.'}
            </p>
          </div>
          {activityUser ? (
            <Button variant="outline" onClick={() => loadActivity(null)}>Clear</Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          {activityLoading ? (
            <div className="text-sm text-slate-500">Loading recent activity...</div>
          ) : activityUser && activities.length ? (
            activities.map((entry) => (
              <div key={entry.id} className="rounded-2xl border border-slate-200 p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <div className="font-medium text-slate-900">{entry.label || entry.action}</div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                    {entry.actor_name ? <span>Actor: {entry.actor_name}</span> : null}
                    {entry.package_name ? <span>Package: {entry.package_name}</span> : null}
                    {entry.days ? <span>Days: {entry.days}</span> : null}
                    {entry.subscription_status ? <span>Status: {entry.subscription_status}</span> : null}
                  </div>
                </div>
                <div className="text-xs text-slate-500">{formatDate(entry.created_at)}</div>
              </div>
            ))
          ) : activityUser ? (
            <div className="text-sm text-slate-500">No recent budget activity found for this user.</div>
          ) : (
            <div className="text-sm text-slate-500">No user selected.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
